import {
  searchShippingOrders,
  refetchShippingOrders,
  getShippingDetail,
  getLotListByItem,
  scanLot,
  shipProduct,
} from "src/apis/shippingApi";
import { PDA1130_R01Dto, PDA1130_R01SearchParams } from "./PDA1130_R01Dto";
import usePDA1130_R01Store, { InspectedItem } from "./usePDA1130_R01Store";
import { useToast } from "src/hooks/useToast";

export const usePDA1130_R01Content = () => {
  const {
    searchParams,
    setShippingOrderList,
    setSelectedShippingOrder,
    setShippingDetail,
    setShowList,
    setLoading,
    resetDetail,
    setSelectedItemForLot,
    setLotList,
    lotNo,
    setLotNo,
    setScannedLot,
    scannedLot,
    shippingDetail,
    inspectedItems,
    addInspectedItem,
    clearInspectedItems,
  } = usePDA1130_R01Store();

  const { showToast } = useToast();

  const buildSearchParams = (): PDA1130_R01SearchParams => {
    const params: PDA1130_R01SearchParams = {};
    if (searchParams.orderNo) params.orderNo = searchParams.orderNo;
    if (searchParams.startDate) params.startDate = searchParams.startDate.split("T")[0];
    if (searchParams.endDate) params.endDate = searchParams.endDate.split("T")[0];
    return params;
  };

  /**
   * 목록 조회
   */
  const fetchOrders = async (
    apiFn: (params: PDA1130_R01SearchParams) => Promise<any>
  ) => {
    try {
      setLoading(true);
      const params = buildSearchParams();
      const response = await apiFn(params);
      const data = response?.data?.data || [];
      setShippingOrderList(data);
      if (data.length === 0) showToast("warning", "조회 결과가 없습니다.");
    } catch (error) {
      console.error("출하지시 조회 실패:", error);
      setShippingOrderList([]);
      showToast("error", "출하지시 조회에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => fetchOrders(searchShippingOrders);
  const handleRefetch = () => fetchOrders(refetchShippingOrders);

  /**
   * 출하지시 선택 → 상세 화면으로 이동
   */
  const handleSelectOrder = async (order: PDA1130_R01Dto) => {
    if (!order.orderNo) {
      console.error("출하지시 번호가 없습니다.");
      return;
    }

    try {
      setLoading(true);

      const response = await getShippingDetail(order.orderNo);
      const detail = response?.data?.data;

      if (!detail) throw new Error("출하지시 상세 정보를 찾을 수 없습니다.");

      if (!detail.itemList || detail.itemList.length === 0) {
        showToast("warning", "출고할 제품이 없습니다.");
        return;
      }

      const firstItem = detail.itemList[0];
      const warehouseCd = detail.warehouseCd || "100";

      let lotData: any[] = [];
      try {
        const lotResponse = await getLotListByItem(firstItem.itemCd, warehouseCd);
        lotData = lotResponse?.data?.data || [];
      } catch (lotError) {
        console.error("LOT 목록 조회 실패:", lotError);
        showToast("error", "LOT 목록 조회에 실패했습니다.");
        return;
      }

      if (lotData.length === 0) {
        showToast("warning", "조회 가능한 LOT가 없습니다.");
        return;
      }

      setSelectedShippingOrder(order);
      setShippingDetail(detail);
      setSelectedItemForLot(firstItem.itemCd);
      setLotList(lotData);
      setShowList(false);
    } catch (error: any) {
      console.error("출하지시 상세 조회 실패:", error);
      showToast("error", error?.response?.data?.message || "출하지시 상세 조회에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * 목록으로 돌아가기
   */
  const handleBackToList = () => {
    const { inspectedItems, clearInspectedItems } = usePDA1130_R01Store.getState();

    if (inspectedItems.length > 0) {
      const confirm = window.confirm(
        `검수 대기중인 ${inspectedItems.length}건의 항목이 있습니다.\n저장하지 않고 나가시겠습니까?`
      );
      if (!confirm) return;
      clearInspectedItems();
    }

    resetDetail();
    setShowList(true);
  };

  /**
   * LOT 스캔
   * ① 품목이 출하지시 목록에 있는지 검증
   * ② 잔여지시량(orderQty - inspQty) 계산
   * ③ 현세션 검수량 반영한 실제 가능 수량 계산
   * ④ LOT 잔여재고(lotQty - 현세션 동일LOT 검수량) 계산
   * ⑤ MIN(잔여지시량, LOT잔여재고) → 가능수량 0 이하면 차단
   * ⑥ scannedLot.lotQty를 최종 가능 수량으로 세팅
   */
  const handleScanLot = async () => {
    const trimmedLotNo = lotNo.trim();
    if (!trimmedLotNo) {
      showToast("error", "LOT 번호를 입력하세요.");
      return;
    }

    try {
      setLoading(true);

      const response = await scanLot(trimmedLotNo);
      const lotData = response?.data?.data;

      if (!lotData) throw new Error("LOT 정보를 찾을 수 없습니다.");

      const matchingItems =
        shippingDetail?.itemList?.filter((item) => item.itemCd === lotData.itemCd) ?? [];

      if (matchingItems.length === 0) {
        showToast("error", "해당 출고 목록에 없는 LOT입니다.");
        setScannedLot(null);
        return;
      }

      const remainOrderQty = matchingItems.reduce(
        (sum, item) => sum + item.orderQty - item.inspQty,
        0
      );

      const alreadyInspectedQty = inspectedItems
        .filter((item) => item.itemCd === lotData.itemCd)
        .reduce((sum, item) => sum + item.shipQty, 0);

      const canQtyByOrder = remainOrderQty - alreadyInspectedQty;

      if (canQtyByOrder <= 0) {
        showToast(
          "error",
          `지시 잔량이 없습니다. (잔여지시량: ${remainOrderQty}, 현검수: ${alreadyInspectedQty})`
        );
        setScannedLot(null);
        return;
      }

      const alreadyLotQty = inspectedItems
        .filter((item) => item.lotNo === lotData.lotNo)
        .reduce((sum, item) => sum + item.shipQty, 0);

      const remainLotQty = lotData.lotQty - alreadyLotQty;

      if (remainLotQty <= 0) {
        showToast(
          "error",
          `LOT 잔여재고가 없습니다. (LOT재고: ${lotData.lotQty}, 기검수: ${alreadyLotQty})`
        );
        setScannedLot(null);
        return;
      }

      const finalCanQty = Math.min(canQtyByOrder, remainLotQty);

      setScannedLot({ ...lotData, lotQty: finalCanQty });

      if (alreadyLotQty > 0 || alreadyInspectedQty > 0) {
        showToast(
          "success",
          `LOT 스캔 완료 (가능수량: ${finalCanQty}, 지시잔량: ${canQtyByOrder}, LOT잔여: ${remainLotQty})`
        );
      } else {
        showToast("success", "LOT 스캔 완료");
      }
    } catch (error: any) {
      console.error("LOT 스캔 실패:", error);
      showToast("error", error?.response?.data?.message || "LOT 스캔에 실패했습니다.");
      setScannedLot(null);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 검수 (상태만 저장)
   * ① 수량 0 이하 체크
   * ② 입력 수량 > scannedLot.lotQty(최종가능수량) 체크
   * ③ 잔여지시량 초과 체크 (현세션 검수량 + 입력수량 > 잔여지시량)
   */
  const handleInspect = (shipQty: number) => {
    if (!shippingDetail || !scannedLot) {
      showToast("error", "필요한 정보가 없습니다.");
      return;
    }

    const matchingItems =
      shippingDetail.itemList?.filter((item) => item.itemCd === scannedLot.itemCd) ?? [];

    if (matchingItems.length === 0) {
      showToast("error", "해당 출고 목록에 없는 LOT입니다.");
      return;
    }

    if (shipQty <= 0) {
      showToast("error", "출고수량은 0보다 커야 합니다.");
      return;
    }

    if (shipQty > scannedLot.lotQty) {
      showToast("error", `가능 수량을 초과합니다. (가능: ${scannedLot.lotQty})`);
      return;
    }

    const remainOrderQty = matchingItems.reduce(
      (sum, item) => sum + item.orderQty - item.inspQty,
      0
    );

    const alreadyInspectedQty = inspectedItems
      .filter((item) => item.itemCd === scannedLot.itemCd)
      .reduce((sum, item) => sum + item.shipQty, 0);

    if (alreadyInspectedQty + shipQty > remainOrderQty) {
      const canQty = remainOrderQty - alreadyInspectedQty;
      showToast(
        "error",
        `지시 잔량을 초과할 수 없습니다. (잔여지시량: ${remainOrderQty}, 현검수: ${alreadyInspectedQty}, 가능: ${canQty})`
      );
      return;
    }

    const inspectedItem: InspectedItem = {
      lotNo: scannedLot.lotNo,
      itemCd: scannedLot.itemCd,
      itemNm: scannedLot.itemNm,
      shipQty: shipQty,
      warehouseCd: scannedLot.warehouseCd,
      locationCd: scannedLot.locationCd,
    };

    addInspectedItem(inspectedItem);
    showToast("success", `검수 완료 (${inspectedItems.length + 1}건)`);

    setLotNo("");
    setScannedLot(null);
  };

  /**
   * 저장 (검수된 모든 항목 일괄 API 호출)
   */
  const handleSaveAll = async () => {
    if (!shippingDetail) {
      showToast("error", "출하지시 정보가 없습니다.");
      return;
    }

    if (inspectedItems.length === 0) {
      showToast("error", "검수된 항목이 없습니다.");
      return;
    }

    try {
      setLoading(true);

      const itemLotMap = new Map<
        string,
        Array<{ lotNo: string; inputOutQty: number; locationCd: string }>
      >();

      inspectedItems.forEach((item) => {
        if (!itemLotMap.has(item.itemCd)) itemLotMap.set(item.itemCd, []);
        itemLotMap.get(item.itemCd)!.push({
          lotNo: item.lotNo,
          inputOutQty: item.shipQty,
          locationCd: item.locationCd || "",
        });
      });

      const itemLotList = Array.from(itemLotMap.entries()).map(([itemCd, lotList]) => ({
        itemCd,
        lotList,
      }));

      const warehouseCdForShip =
        inspectedItems[0]?.warehouseCd || shippingDetail.warehouseCd || "100";
      const logicCenter = shippingDetail.logicCenter || "";

      await shipProduct({
        orderNo: shippingDetail.orderNo,
        warehouseCd: warehouseCdForShip,
        logicCenter,
        remark: "",
        itemLotList,
      });

      const totalLots = inspectedItems.length;
      const totalQty = inspectedItems.reduce((sum, item) => sum + item.shipQty, 0);
      showToast(
        "success",
        `제품 출고 완료 (LOT ${totalLots}건, 총 ${totalQty.toLocaleString()}개)`
      );

      clearInspectedItems();

      const response = await getShippingDetail(shippingDetail.orderNo);
      const detail = response?.data?.data;
      if (detail) {
        setShippingDetail(detail);

        const firstItem = detail.itemList?.[0];
        if (firstItem) {
          try {
            const lotResponse = await getLotListByItem(firstItem.itemCd, warehouseCdForShip);
            setLotList(lotResponse?.data?.data || []);
          } catch (e) {
            console.error("LOT 목록 재조회 실패:", e);
          }
        }
      }
    } catch (error: any) {
      console.error("제품 출고 실패:", error);
      showToast("error", error?.response?.data?.message || "제품 출고에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * 취소 (검수 대기 목록 초기화)
   */
  const handleCancel = () => {
    if (inspectedItems.length > 0) {
      const confirm = window.confirm(
        `검수 대기중인 ${inspectedItems.length}건의 항목이 있습니다.\n모두 취소하시겠습니까?`
      );
      if (!confirm) return;
      clearInspectedItems();
      showToast("info", "검수 대기 목록이 초기화되었습니다.");
    }

    setLotNo("");
    setScannedLot(null);
  };

  return {
    handleSearch,
    handleRefetch,
    handleSelectOrder,
    handleBackToList,
    handleScanLot,
    handleInspect,
    handleSaveAll,
    handleCancel,
  };
};
